import { useEffect, useState } from 'react'

import {
  Avatar,
  Box,
  Icon,
  Spinner,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  AtsCompletedCalendarSyncFragment,
  AtsGetInterviewAttendeesDocument,
  AtsInterviewAttendeeFragment,
  AtsScheduleInterviewOrganizersDocument,
  EmployeeForSelectFragment,
} from 'generated/resources/graphql'

import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { EntitySelectorWrapper } from 'modules/employees/components/EmployeeSelector/EntitySelectorWrapper'

import { CreateInterviewData } from './CreateInterviewData'

interface Props {
  data: CreateInterviewData
  onDataChanged: (data: CreateInterviewData) => void
  calendarSyncsByAccessId: { [id: string]: AtsCompletedCalendarSyncFragment }
}

const requiresLocation = (interviewType?: string) => interviewType == 'in_person'

interface AttendeeProps {
  access: AtsInterviewAttendeeFragment
  hasCalendar: boolean
  organizer: boolean
}

const Attendee = ({ access, hasCalendar, organizer }: AttendeeProps) => {
  const [open, toggle] = useToggle(false)
  return (
    <Box key={access.id} flexDirection="row" gap="s8" alignItems="center">
      <Avatar size="s32" url={buildUrl(access.avatarFull)} label={access.fullName} />
      <Box>
        <Text.Normal>{access.fullName}</Text.Normal>
      </Box>
      {!hasCalendar && (
        <Tooltip.Alt
          attachment="middle left"
          open={open}
          toggle={toggle}
          label={i18n.t('ats.schedule_interview.steps.team_location.attendee_has_not_calendar')}
        >
          <Icon.Medium icon="info-circle-line" color="grey800" />
        </Tooltip.Alt>
      )}
      {organizer && (
        <Tag type="viridian">{i18n.t('ats.schedule_interview.steps.team_location.organizer')}</Tag>
      )}
    </Box>
  )
}

export const isTeamLocationCompleted = (data: CreateInterviewData) =>
  !!data.organizerAccessId &&
  !!data.attendeeIds.length &&
  !!data.interviewType &&
  (!requiresLocation(data.interviewType) || !!data.customLocation)

export const TeamLocationStep = ({ data, onDataChanged, calendarSyncsByAccessId }: Props) => {
  const organizerAccessIds = Object.keys(calendarSyncsByAccessId)

  const { data: organizersData } = useQuery(AtsScheduleInterviewOrganizersDocument, {
    variables: { accessIds: organizerAccessIds },
    skip: organizerAccessIds.length === 0,
  })
  const { data: attendeesData, loading } = useQuery(AtsGetInterviewAttendeesDocument, {
    variables: { attendeeIds: data.attendeeIds },
    skip: data.attendeeIds.length === 0,
  })

  const [selectedOrganizer, setSelectedOrganizer] = useState<EmployeeForSelectFragment | null>(null)
  const [selectedAttendees, setSelectedAttendees] = useState<EmployeeForSelectFragment[]>([])

  const organizerEmployeeIds =
    organizersData?.employees.employeesConnection.nodes.map((emp) => emp.id) || []

  useEffect(() => {
    if (attendeesData?.apiCore.accessesConnection.nodes) {
      const mappedAttendees = attendeesData.apiCore.accessesConnection.nodes.map((attendee) => ({
        id: attendee.id,
        firstName: attendee.employee?.firstName ?? '',
        fullName: attendee.employee?.fullName ?? '',
        lastName: attendee.employee?.lastName ?? '',
        access: attendee,
      }))

      setSelectedAttendees(mappedAttendees)
    }
  }, [attendeesData])

  useEffect(() => {
    const employees = organizersData?.employees.employeesConnection.nodes || []

    if (employees.length === 0) {
      setSelectedOrganizer(null)
      return
    }

    let organizerToSelect: (typeof employees)[0] | undefined

    if (data.organizerAccessId) {
      organizerToSelect = employees.find((emp) => emp.access.id === data.organizerAccessId)
    }

    if (!organizerToSelect) return

    const organizerForSelector: EmployeeForSelectFragment = {
      id: organizerToSelect.id,
      firstName: organizerToSelect.firstName,
      fullName: organizerToSelect.fullName,
      lastName: organizerToSelect.lastName,
      access: organizerToSelect.access,
    }

    setSelectedOrganizer(organizerForSelector)

    if (!data.organizerAccessId) {
      onDataChanged({
        ...data,
        organizerAccessId: organizerToSelect.access.id,
        attendeeIds: [...data.attendeeIds, organizerToSelect.access.id],
      })

      setSelectedAttendees((prev) => {
        if (prev.some((att) => att.access.id === organizerToSelect.access.id)) {
          return prev
        }
        return [...prev, organizerForSelector]
      })
    }
  }, [data.organizerAccessId, organizersData, data, onDataChanged])

  const onSelectOrganizer = (employee: EmployeeForSelectFragment | null) => {
    setSelectedOrganizer(employee)

    if (!employee) {
      onDataChanged({
        ...data,
        organizerAccessId: undefined,
      })
      return
    }

    const organizerAccessId = employee.access.id

    const updatedAttendeeIds = data.attendeeIds.includes(organizerAccessId)
      ? data.attendeeIds
      : [...data.attendeeIds, organizerAccessId]

    onDataChanged({
      ...data,
      organizerAccessId,
      attendeeIds: updatedAttendeeIds,
    })

    if (!selectedAttendees.some((att) => att.access.id === organizerAccessId)) {
      setSelectedAttendees((prev) => [...prev, employee])
    }
  }

  const onSelectAttendees = (employees: EmployeeForSelectFragment[]) => {
    setSelectedAttendees(employees)
    const employeeIds = employees.map((emp) => emp.access.id)

    const finalAttendeeIds =
      data.organizerAccessId && !employeeIds.includes(data.organizerAccessId)
        ? [...employeeIds, data.organizerAccessId]
        : employeeIds

    onDataChanged({
      ...data,
      attendeeIds: finalAttendeeIds,
    })
  }

  const attendees = attendeesData?.apiCore.accessesConnection.nodes || []

  const typeOptions = [
    {
      value: 'online',
      label: i18n.t('ats.schedule_interview.steps.team_location.interview_types.online'),
    },
    {
      value: 'in_person',
      label: i18n.t('ats.schedule_interview.steps.team_location.interview_types.in_person'),
    },
    {
      value: 'telephone',
      label: i18n.t('ats.schedule_interview.steps.team_location.interview_types.telephone'),
    },
  ]

  if (loading) {
    return (
      <Box alignItems="center" justifyContent="center" style={{ minHeight: '200px' }}>
        <Spinner />
      </Box>
    )
  }

  return (
    <Box gap="s24">
      <Box flexDirection="row" gap="s24">
        <EntitySelectorWrapper
          usingAccessIds
          multiple={false}
          label={i18n.t('ats.schedule_interview.steps.team_location.organizer')}
          value={selectedOrganizer}
          onSelect={onSelectOrganizer}
          mandatory
          employeeIds={organizerEmployeeIds}
        />

        <EntitySelectorWrapper
          usingAccessIds
          multiple
          label={i18n.t('ats.schedule_interview.steps.team_location.attendees')}
          value={selectedAttendees}
          onSelect={onSelectAttendees}
          mandatory
        />
      </Box>
      <Box gap="s12">
        {attendees.map((access) => (
          <Attendee
            key={access.id}
            access={access}
            hasCalendar={!!calendarSyncsByAccessId[access.id]}
            organizer={access.id === data.organizerAccessId}
          />
        ))}
      </Box>
      <Box flexDirection="row" gap="s24">
        <SelectField
          label={i18n.t('ats.schedule_interview.steps.team_location.interview_type')}
          options={typeOptions}
          none={i18n.t('ats.schedule_interview.steps.select')}
          mandatory
          value={data.interviewType}
          onChange={(value) =>
            onDataChanged({
              ...data,
              interviewType: value,
            })
          }
        />
        {requiresLocation(data.interviewType) && (
          <TextField
            mandatory
            label={i18n.t('ats.schedule_interview.steps.team_location.custom_location')}
            value={data.customLocation}
            onChange={(value) =>
              onDataChanged({
                ...data,
                customLocation: value,
              })
            }
          />
        )}
      </Box>
      <Box alignItems="flexStart">
        <Box flexDirection="row" alignItems="center" justifyContent="center" gap="s12">
          <Icon.Medium icon="alert-circle-line" color="grey800" />
          <Text.Normal color="grey800" size="200" weight="medium">
            {i18n.t('ats.schedule_interview.steps.team_location.connected_calendar_warn')}
          </Text.Normal>
        </Box>
      </Box>
    </Box>
  )
}
